import _defineProperty from "@babel/runtime/helpers/defineProperty";
// Copyright 1999-2025. WebPros International GmbH. All rights reserved.

import { useContext, useEffect, useRef, createContext } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
class AutoClosableContextApi {
  constructor(ignoreNodesRef) {
    this.ignoreNodesRef = ignoreNodesRef;
    _defineProperty(this, "children", []);
    _defineProperty(this, "addChild", child => {
      this.children.push(child);
    });
    _defineProperty(this, "removeChild", child => {
      this.children = this.children.filter(item => item !== child);
    });
    _defineProperty(this, "getIgnoreNodes", () => {
      const nodes = [...this.ignoreNodesRef.current(), ...this.children.map(child => child.getIgnoreNodes()).flat()];
      return nodes.filter(node => node instanceof Node);
    });
  }
}
const AutoClosableContext = /*#__PURE__*/createContext(null);
/**
 * This is helper component for handling outside clicks.
 * @since 0.0.42
 */
const AutoClosable = _ref => {
  let {
    onClose,
    ignoreNodes,
    nodeRef,
    children
  } = _ref;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const getIgnoreNodes = () => [nodeRef?.current ?? null, ...(ignoreNodes?.() ?? [])];
  const ignoreNodesRef = useRef(getIgnoreNodes);
  ignoreNodesRef.current = getIgnoreNodes;
  const parentContext = useContext(AutoClosableContext);
  const autoClosableApi = useRef(new AutoClosableContextApi(ignoreNodesRef)).current;
  useEffect(() => {
    const handleClickOutside = event => {
      const element = event.target;
      const ignoreNodes = autoClosableApi.getIgnoreNodes();
      const isClickOnIgnoredNode = element instanceof Node && ignoreNodes.some(node => node.contains(element));
      if (isClickOnIgnoredNode) {
        return;
      }
      onCloseRef.current?.();
    };
    parentContext?.addChild(autoClosableApi);
    document.body.addEventListener('click', handleClickOutside, {
      capture: true
    });
    return () => {
      parentContext?.removeChild(autoClosableApi);
      document.body.removeEventListener('click', handleClickOutside, {
        capture: true
      });
    };
  }, [parentContext, autoClosableApi]);
  return /*#__PURE__*/_jsx(AutoClosableContext.Provider, {
    value: autoClosableApi,
    children: children
  });
};
export default AutoClosable;